import uniqBy from 'lodash.uniqby';

import CART_ITEM from '../../constants/cartItems';

/**
 * Determines if all cart items belong to a single vendor or multiple.
 * Note that an undefined vendor might mean users are combining Waldo items (Waldograms) + vendor items.
 *
 * @param {Array<Object>} cart The cart items.
 * @returns {boolean} Whether or not all items belong to a single vendor.
 */
export const containSingleVendorItems = cart => {
  const filteredCart = cart.filter(
    item => item.type !== CART_ITEM.PRINT_ORDER_SERVICE,
  );

  const [item] = filteredCart;

  return !filteredCart.find(
    cartItem => cartItem.vendor?.id !== item?.vendor?.id,
  );
};

/**
 * Whether the cart holds a Digital Photo Pass (album folder or album
 * subscription). This is the condition under which SUBSCRIPTIONS promo codes
 * apply.
 *
 * @param {Array<Object>} cart The cart items.
 * @returns {boolean} Whether a subscription item is in the cart.
 */
export const cartHasSubscriptionItems = cart =>
  (cart ?? []).some(item =>
    [
      CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION,
      CART_ITEM.ALBUM_SUBSCRIPTION,
    ].includes(item.type),
  );

/**
 * Resolves the event (root album folder) a cart item belongs to.
 * Prefers the album membership group, then falls back to the album / album
 * folder attached directly to the item (some flows add items without a group).
 *
 * @param {Object} item The cart item.
 * @returns {string|null} The event id, or null when it cannot be determined.
 */
export const getCartItemEventId = item => {
  const { albumMembershipGroup, album, albumFolder } = item ?? {};

  if (albumMembershipGroup) {
    if (albumMembershipGroup.is_album) {
      return (
        albumMembershipGroup.album?.root_album_folder?.id ??
        albumMembershipGroup.album?.id ??
        null
      );
    }

    return (
      albumMembershipGroup.album_folder?.root?.id ??
      albumMembershipGroup.album_folder?.id ??
      albumMembershipGroup.id ??
      null
    );
  }

  return (
    album?.root_album_folder?.id ??
    albumFolder?.root?.id ??
    albumFolder?.id ??
    null
  );
};

/**
 * Determines if all cart items belong to a single event.
 * Items whose event cannot be determined (e.g. Waldograms, or items added
 * while their album data was still loading) are ignored.
 *
 * @param {Array<Object>} cart The cart items.
 * @returns {boolean} Whether or not all items belong to a single event
 */
export const containSingleEventItems = cart => {
  const eventIds = cart
    .filter(item => item.type !== CART_ITEM.PRINT_ORDER_SERVICE)
    .map(getCartItemEventId)
    .filter(Boolean);

  return new Set(eventIds).size <= 1;
};

export const containsTransactionsMissingPrepaidItems = (
  cart,
  prepaidPrintPackagesMissingConfiguration,
  prepaidPhotoPricingsMissingConfiguration,
  prepaidCustomPrintsMissingConfiguration,
) => {
  if (
    !cart.length ||
    (!prepaidPrintPackagesMissingConfiguration.length &&
      !prepaidPhotoPricingsMissingConfiguration.length &&
      !prepaidCustomPrintsMissingConfiguration.length)
  ) {
    return false;
  }

  let transactionMissingPrepaidItemsFound = false;
  const transactionIdsMissingConfiguration = uniqBy(
    [
      ...prepaidPrintPackagesMissingConfiguration,
      ...prepaidPhotoPricingsMissingConfiguration,
      ...prepaidCustomPrintsMissingConfiguration,
    ]
      .map(({ transaction }) => transaction.id)
      .filter(Boolean),
  );

  cart.forEach(item => {
    if (transactionMissingPrepaidItemsFound) {
      return;
    }

    if (
      !item.purchasedPrintPackage &&
      !item.purchasedPhotoPricing &&
      !item.purchasedCustomPrint
    ) {
      return;
    }

    const transactionId =
      item.purchasedPrintPackage?.transaction?.id ??
      item.purchasedPhotoPricing?.transaction?.id ??
      item.purchasedCustomPrint?.transaction?.id;

    if (!transactionId) {
      return;
    }

    if (transactionIdsMissingConfiguration.includes(transactionId)) {
      transactionMissingPrepaidItemsFound = true;
    }
  });

  return transactionMissingPrepaidItemsFound;
};

/**
 * Prices the print partner shipping options a customer can pick from, out of
 * the checkout price computed with and without each option. The platform
 * replaces the order shipping cost with the option's one, so an option is
 * offered when it prices and changes the shipping cost.
 *
 * @param {Array<Object>} shippingOptions The print partner shipping options.
 * @param {Object} basePricing The checkout price without any shipping option.
 * @param {Object<string, Object>} optionPricings The checkout price per shipping option id.
 * @returns {Array<Object>} The offered shipping options with their `shipping_price`.
 */
export const getPricedShippingOptions = (
  shippingOptions,
  basePricing,
  optionPricings,
) => {
  const baseShippingCost = basePricing?.total_shipping_cost;

  if (!baseShippingCost) {
    return [];
  }

  return (shippingOptions ?? []).reduce((pricedOptions, shippingOption) => {
    const shippingCost =
      optionPricings?.[shippingOption.id]?.total_shipping_cost;

    if (!shippingCost || shippingCost.value === baseShippingCost.value) {
      return pricedOptions;
    }

    return [
      ...pricedOptions,
      {
        ...shippingOption,
        shipping_price: shippingCost,
      },
    ];
  }, []);
};
